import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import { TurnstileComponent } from '../../user-controls/turnstile/turnstile.component';
import { DiscountCodeService } from '../../services/discount-code.service';
import { SettingsService } from '../../services';

@Component({
  templateUrl: './generate-order-discount-code.component.html',
  styleUrls: ['./generate-order-discount-code.component.scss'],
})
export class GenerateOrderDiscountCodeComponent implements OnInit {
  @ViewChild('turnstileWidget') turnstileWidget!: TurnstileComponent;

  turnstileSiteKey = '';
  turnstileToken: string | null = null;

  formReady: boolean = false;
  generateForm: FormGroup;
  codeGenerated: boolean = false;
  loading: boolean = false;
  showErrorDialog: boolean = false;
  referrer: string = null;

  constructor(
    private fb: FormBuilder,
    private discountCodeService: DiscountCodeService,
    private settingsService: SettingsService,
    private route: ActivatedRoute,
  ) {
    this.generateForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      forename: ['', Validators.required],
      surname: ['', Validators.required],
    });
  }

  async ngOnInit(): Promise<void> {
    this.turnstileSiteKey = await this.settingsService.getTurnstileSiteKey();

    this.route.queryParamMap.subscribe(async (queryParams) => {
      if (queryParams.get('ref') !== null) {
        this.referrer = queryParams.get('ref');
      }

      this.formReady = true;
    });
  }

  onTurnstileVerified(token: string): void {
    this.turnstileToken = token;
  }

  onTurnstileExpired(): void {
    this.turnstileToken = null;
  }

  onTurnstileError(): void {
    this.turnstileToken = null;
  }

  async onSubmit(): Promise<void> {
    this.generateForm.markAllAsTouched();

    if (this.loading) {
      return;
    }

    if (!(this.generateForm.valid && this.turnstileToken)) {
      return;
    }

    const payload = {
      ...this.generateForm.value,
      cfTurnstileResponse: this.turnstileToken,
      referrer: this.referrer,
    };

    try {
      this.loading = true;
      await this.discountCodeService.generateDiscountCode(payload);
      this.codeGenerated = true;
    } catch (error) {
      console.error('Error generating code:', error);
      if (error instanceof HttpErrorResponse) {
        if (error.status === 409) {
          this.generateForm.controls.email.setErrors({ duplicate: true });
          return;
        }
      }
      this.showErrorDialog = true;
    } finally {
      this.loading = false;
      this.turnstileWidget.reset();
      this.turnstileToken = null;
    }
  }

  getEmailErrorMessage() {
    const control = this.generateForm.controls['email'];

    if (control.valid) {
      return null;
    }

    if (!control.touched) {
      return null;
    }

    if (control.hasError('required') || control.hasError('email')) {
      return 'Please enter a valid email address';
    }

    if (control.hasError('duplicate')) {
      return 'Email address already used';
    }

    return null;
  }

  showForenameError() {
    return (
      this.generateForm.controls['forename'].invalid &&
      this.generateForm.controls['forename'].touched
    );
  }

  showSurnameError() {
    return (
      this.generateForm.controls['surname'].invalid && this.generateForm.controls['surname'].touched
    );
  }
}
